import React from 'react';
import { translate } from '../../../services/translate';
import { NavigationProp, RouteProp, useNavigation } from '@react-navigation/native';
import { CRMParamList } from '../../../typings/Params';
import { CameraType, StorageTypeFilter, AlgorithmFilter } from '../../../schemas/cameras';
import { Manufacturer } from '../../../schemas/crm';
import FilterModal from '../../../components/ModernFilters/FilterModal';
import { useForm } from 'react-hook-form';
import CompanyList from '../../../components/ModernFilters/crm/CompanyList';
import { useCurrentApp } from '../../../context/CurrentApp';
import RadioList from '../../../components/ModernFilters/RadioList';
import CameraTypeList from '../../../components/ModernFilters/CameraTypeList';
import CameraAlgorithmList from '../../../components/ModernFilters/CameraAlgorithmList';
import CameraTagList from '../../../components/ModernFilters/cameras/CameraTagList';
import CameraManufacturerList from '../../../components/ModernFilters/CameraManufacturerList';
import InstallationCompanyList from '../../../components/ModernFilters/access/InstallationCompanyList';
import InternetCompanyList from '../../../components/ModernFilters/access/InternetCompanyList';

interface Props {
    filters: CRMParamList['Management'];
    setFilters: React.Dispatch<React.SetStateAction<CRMParamList['Management']>>;
}

function Filter({ filters, setFilters }: Props) {
    const navigation = useNavigation<NavigationProp<RouteProp<CRMParamList, 'Management'>>>();
    const { isParentApp } = useCurrentApp();

    type FiltersForm = {
        companyId: number | null;
        status: 'onlyOffline' | 'onlyOnline' | null;
        type: CameraType | null;
        storage: StorageTypeFilter | null;
        algorithm: AlgorithmFilter | null;
        tagsIds: number[];
        manufacturer: Manufacturer | null;
        installationCompanyId: number | null;
        internetCompanyId: number | null;
    };

    const form = useForm<FiltersForm>({
        mode: 'onChange',
        defaultValues: {
            companyId: filters.companyId ?? null,
            status: filters.offline ? 'onlyOffline' : (filters.online ? 'onlyOnline' : null),
            type: filters.type ?? null,
            storage: filters.storageType ?? null,
            algorithm: filters.hasFacial ? 'hasFacial' : (filters.hasLpr ? 'hasLpr' : (filters.hasAreaInvasion ? 'hasAreaInvasion' : null)),
            tagsIds: filters.tagsIds ?? [],
            manufacturer: filters.manufacturer ?? null,
            installationCompanyId: filters.installationCompanyId ?? null,
            internetCompanyId: filters.internetCompanyId ?? null,
        },
    });

    const handleFilter = () => {

        const formValues = form.getValues();

        const params = {
            companyId: formValues.companyId ?? undefined,
            offline: formValues.status === 'onlyOffline' ? true : undefined,
            online: formValues.status === 'onlyOnline' ? true : undefined,
            type: formValues.type ?? undefined,
            storageType: formValues.storage ?? undefined,
            hasFacial: formValues.algorithm === 'hasFacial' || undefined,
            hasLpr: formValues.algorithm === 'hasLpr' || undefined,
            hasAreaInvasion: formValues.algorithm === 'hasAreaInvasion' || undefined,
            tagsIds: formValues.tagsIds.length ? formValues.tagsIds : undefined,
            manufacturer: formValues.manufacturer ?? undefined,
            installationCompanyId: formValues.installationCompanyId ?? undefined,
            internetCompanyId: formValues.internetCompanyId ?? undefined,
        };

        setFilters({
            ...filters,
            page: 0,
            ...params,
        });

        navigation.setParams({
            ...filters,
            page: 1,
            ...params,
        });
    };

    return <FilterModal
        form={form}
        onFilters={handleFilter}
        cleanValues={{
            companyId: null,
            status: null,
            type: null,
            storage: null,
            algorithm: null,
            tagsIds: [],
            manufacturer: null,
            installationCompanyId: null,
            internetCompanyId: null,
        }}
        filters={[{
            label: translate('organizational'),
            filters: [{
                componentHandlesCount: true,
                name: 'companyId',
                label: translate('company'),
                component: CompanyList,
                omit: !isParentApp,
                cleans: ['tagsIds', 'installationCompanyId', 'internetCompanyId'],
            }],
        }, {
            label: translate('cameraInfoLabel'),
            filters: [{
                componentHandlesCount: false,
                name: 'status',
                label: translate('status'),
                getCount: RadioList.getCount,
                component: ({ name }) => <RadioList name={name} clearValue={null} items={[
                    { label: translate('online'), value: 'onlyOnline' },
                    { label: translate('offline'), value: 'onlyOffline' },
                ]} />,
            }, {
                componentHandlesCount: true,
                name: 'type',
                component: CameraTypeList,
                label: translate('cameraType'),
            }, {
                componentHandlesCount: false,
                name: 'storage',
                label: translate('storage'),
                getCount: RadioList.getCount,
                component: ({ name }) => <RadioList name={name} clearValue={null} items={[
                    { label: translate('storageCloud'), value: 'cloud' },
                    { label: translate('storageCard'), value: 'card' }
                ]} />,
                omit: !isParentApp,
            }, {
                componentHandlesCount: true,
                name: 'manufacturer',
                label: translate('manufacturer'),
                component: CameraManufacturerList,
            }, {
                componentHandlesCount: false,
                name: 'installationCompanyId',
                label: translate('installationCompany'),
                getCount: InstallationCompanyList.getCount,
                component: ({ name }) => <InstallationCompanyList
                    name={name}
                    companyId={form.watch('companyId')}
                />,
            }, {
                componentHandlesCount: false,
                name: 'internetCompanyId',
                label: translate('internetCompany'),
                getCount: InternetCompanyList.getCount,
                component: ({ name }) => <InternetCompanyList
                    name={name}
                    companyId={form.watch('companyId')}
                />,
            }, {
                componentHandlesCount: true,
                name: 'algorithm',
                label: translate('algorithms'),
                component: CameraAlgorithmList,
            }, {
                componentHandlesCount: false,
                name: 'tagsIds',
                label: translate('tags'),
                getCount: CameraTagList.getCount,
                component: ({ name }) => <CameraTagList
                    name={name}
                    companyId={form.watch('companyId')}
                />,
            }],
        }]}
    />;
}

export default Filter;
